'use client';

import React, { useState } from 'react';
import Link from 'next/link';

const PATHWAY_LAYERS = [
  { n: '1', label: 'ENCOUNTER', ar: 'الظاهر', desc: 'Sensory contact with the Ayah — what you perceive through Hawas (senses).', color: '#2d7a47' },
  { n: '2', label: 'THREE QUESTIONS', ar: 'ثلاثة أسئلة', desc: 'Is it? (Huduth Dhati) · What is it? (Huduth Sifati) · When is/was it? (Huduth Zamani)', color: '#2d8a87' },
  { n: '3', label: 'NAMES FISSION', ar: 'أسماء', desc: 'Which Names and Attributes of Allah compose this reality? The fission process.', color: '#c9a84c' },
  { n: '4', label: 'SUNNAT ALLAH', ar: 'سنة الله', desc: 'The process this thing participates in — each step a divine command executing.', color: '#2d8a87' },
  { n: '5', label: 'ZAHIR → BATIN', ar: 'ظاهر / باطن', desc: 'The surface reality and the hidden reality beneath — the invisible systems sustaining the visible.', color: '#5a5a8a' },
  { n: '6', label: 'HAQQ', ar: 'حق', desc: 'The truth of what this thing actually is — an Ayah of Allah in creation.', color: '#c9a84c' },
  { n: '7', label: 'AMANAH + BRIDGES', ar: 'أمانة', desc: 'The trust inherent in recognition. You have seen — what does this demand?', color: '#2d7a47' },
];

export default function BridgeIRLTransition() {
  const [activeLayer, setActiveLayer] = useState<number | null>(null);

  return (
    <section className="py-20 border-t" style={{ background: 'rgba(6,13,18,0.85)', borderColor: 'rgba(139,105,20,0.15)', color: '#d4c5a9', fontFamily: 'Georgia, serif' }}>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">

        {/* Transition label */}
        <div className="flex items-center gap-4 mb-12">
          <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(139,105,20,0.3))' }} />
          <div className="text-center">
            <p className="font-mono text-[9px] tracking-widest uppercase mb-1" style={{ color: 'rgba(139,105,20,0.5)' }}>Bridge 02 · Passage</p>
            <p className="font-mono text-xs tracking-widest uppercase" style={{ color: 'rgba(245,240,232,0.25)' }}>Ayat in Creation → IRL Application</p>
          </div>
          <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, rgba(139,105,20,0.3), transparent)' }} />
        </div>

        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="font-mono text-2xl md:text-3xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
            FROM SEEING<br /><span style={{ color: 'rgba(179,138,42,0.8)' }}>TO DOING</span>
          </h2>
          <p className="font-mono text-sm max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>
            The shift from recognition to utility. Perceiving signs translates into real-world action. The inner circle of awareness expands outward into the world of application.
          </p>
        </div>

        {/* Platform flow */}
        <div className="flex items-center justify-center gap-3 flex-wrap mb-12">
          {[
            { label: 'KEYS TO KULLI HAAL', sub: 'Sacred Language', color: '#c9a84c' },
            { label: '→', sub: '', color: 'rgba(201,168,76,0.3)' },
            { label: 'IRL LENS', sub: 'Recognition Tool', color: '#2d8a87' },
            { label: '→', sub: '', color: 'rgba(201,168,76,0.3)' },
            { label: 'MUSLIM RESOURCE GUIDE', sub: 'Utility for Action', color: '#2d7a47' },
          ].map((node, i) => (
            node.label === '→' ? (
              <div key={i} className="font-mono text-xl" style={{ color: node.color }}>→</div>
            ) : (
              <div key={i} className="px-4 py-3 text-center" style={{ border: `1px solid ${node.color}30`, background: `${node.color}08`, minWidth: 160 }}>
                <div className="font-mono text-[8px] tracking-widest uppercase mb-1" style={{ color: node.color }}>{node.sub}</div>
                <div className="font-mono text-xs font-bold" style={{ color: '#f0ece4' }}>{node.label}</div>
              </div>
            )
          ))}
        </div>

        {/* 7-layer pathway */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="font-mono text-[9px] tracking-widest uppercase text-center mb-6" style={{ color: 'rgba(201,168,76,0.5)' }}>The Recognition Pathway</div>
          <div className="flex flex-col gap-2">
            {PATHWAY_LAYERS?.map((layer, i) => (
              <div key={layer?.n}
                style={{ border: `1px solid ${activeLayer === i ? layer?.color + '50' : 'rgba(61,54,39,0.3)'}`, borderRadius: 4, overflow: 'hidden', transition: 'all 0.25s', background: activeLayer === i ? `${layer?.color}08` : 'rgba(10,25,41,0.1)', cursor: 'pointer' }}
                onClick={() => setActiveLayer(activeLayer === i ? null : i)}>
                <div style={{ padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: `${layer?.color}18`, border: `1px solid ${layer?.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: layer?.color, flexShrink: 0, fontWeight: 'bold' }}>{layer?.n}</div>
                  <div style={{ flex: 1, fontSize: 10, color: layer?.color, letterSpacing: '.1em', textTransform: 'uppercase' }}>{layer?.label}</div>
                  <div style={{ fontSize: 13, color: `${layer?.color}80` }}>{layer?.ar}</div>
                  <div style={{ fontSize: 10, color: '#7a6f5d' }}>{activeLayer === i ? '−' : '+'}</div>
                </div>
                {activeLayer === i && (
                  <div style={{ padding: '0 12px 10px 42px', fontSize: 12, color: '#d4c5a9', lineHeight: 1.6, borderTop: `1px solid ${layer?.color}20` }}>
                    {layer?.desc}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-6">
          <Link href="/inner-reality-lens"
            className="font-mono text-xs tracking-widest uppercase px-6 py-3 transition-colors"
            style={{ background: 'rgba(139,105,20,0.85)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.35)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.85)'; }}
          >
            Enter the Inner Reality Lens →
          </Link>
          <Link href="/bridge-irl-application"
            className="font-mono text-[10px] tracking-widest uppercase transition-colors border-b"
            style={{ color: 'rgba(245,240,232,0.3)', borderColor: 'rgba(139,105,20,0.2)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.7)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.3)'; }}
          >
            Full Bridge 02 →
          </Link>
        </div>
      </div>
    </section>
  );
}
